import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Loader2,
  RefreshCw,
  Trash2,
  Plus,
} from "lucide-react";
import {
  addVerificationSample,
  bulkAddVerificationSamples,
  deleteVerificationSample,
  getVerification,
  refreshVerificationSample,
  startVerification,
  type VerificationExpected,
  type VerificationOutcome,
  type VerificationSampleSummary,
} from "@/api/verification";
import { ApiError } from "@/api/client";
import { useVerificationProgress } from "@/hooks/useEventStream";

/**
 * Verification corpus panel for the /checks/:id edit page.
 *
 * Reads {samples, latest_run} from GET /verification, joins them by
 * sample_id on the client, and live-patches per-sample verdicts via the
 * `verification_progress` SSE event. The "Run verification" button POSTs
 * to /runs and the rest happens asynchronously through SSE — the API
 * thread does the work; the panel just reflects state.
 */
export function VerificationCorpusPanel({ checkId }: { checkId: string }) {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["verification", checkId],
    queryFn: () => getVerification(checkId),
    refetchOnWindowFocus: false,
  });

  const progress = useVerificationProgress(checkId);
  const samples = query.data?.samples ?? [];
  const latestRun = query.data?.latest_run ?? null;

  // Per-sample outcomes from latest_run, patched in real time with SSE
  // events. The terminal SSE frame (latest_outcome=null) invalidates the
  // query, after which `latestRun.outcomes` carries the canonical state.
  const outcomesBySample = useMemo(() => {
    const map = new Map<string, VerificationOutcome>();
    for (const o of latestRun?.outcomes ?? []) map.set(o.sample_id, o);
    if (progress?.latest_outcome) {
      map.set(progress.latest_outcome.sample_id, {
        sample_id: progress.latest_outcome.sample_id,
        at: new Date().toISOString(),
        verdict: progress.latest_outcome.verdict,
        matched_tags: progress.latest_outcome.matched_tags,
        evidence: progress.latest_outcome.evidence,
        word_hits: progress.latest_outcome.word_hits,
        unreachable_reason: progress.latest_outcome.unreachable_reason,
        harness_error: progress.latest_outcome.harness_error,
      });
    }
    return map;
  }, [latestRun, progress]);

  const isRunning =
    latestRun?.status === "running" ||
    (progress !== undefined && progress.latest_outcome !== null);
  const completed = progress?.completed ?? latestRun?.completed ?? 0;
  const total = progress?.total ?? latestRun?.total ?? samples.length;

  const positives = samples.filter((s) => s.expected === "positive");
  const negatives = samples.filter((s) => s.expected === "negative");

  const startMut = useMutation({
    mutationFn: () => startVerification(checkId),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["verification", checkId] }),
    onError: (e: unknown) => {
      if (e instanceof ApiError && e.status === 409) {
        toast.error("A verification is already running for this check.");
      } else {
        toast.error(e instanceof Error ? e.message : "Failed to start verification");
      }
    },
  });

  return (
    <section className="card p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Verification corpus</h2>
        <div className="flex items-center gap-2">
          {isRunning ? (
            <div className="text-xs text-muted">
              {completed} / {total}
            </div>
          ) : null}
          <button
            className="btn-primary text-sm"
            onClick={() => startMut.mutate()}
            disabled={isRunning || startMut.isPending || samples.length === 0}
          >
            {isRunning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Run verification
          </button>
        </div>
      </div>

      {isRunning && total > 0 ? (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-subtle">
          <div
            className="h-full bg-accent transition-all"
            style={{ width: `${Math.round((completed / total) * 100)}%` }}
          />
        </div>
      ) : null}

      {latestRun?.error ? (
        <div className="rounded-md border border-danger/40 bg-danger/10 p-2 text-xs text-danger">
          Run error: <code>{latestRun.error}</code>
        </div>
      ) : null}

      <SamplesList
        label="POSITIVE (should match this check)"
        expected="positive"
        checkId={checkId}
        samples={positives}
        outcomesBySample={outcomesBySample}
      />
      <SamplesList
        label="NEGATIVE (should NOT match this check)"
        expected="negative"
        checkId={checkId}
        samples={negatives}
        outcomesBySample={outcomesBySample}
      />
    </section>
  );
}

function SamplesList({
  label,
  expected,
  checkId,
  samples,
  outcomesBySample,
}: {
  label: string;
  expected: VerificationExpected;
  checkId: string;
  samples: VerificationSampleSummary[];
  outcomesBySample: Map<string, VerificationOutcome>;
}) {
  const qc = useQueryClient();
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [singleUrl, setSingleUrl] = useState("");

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["verification", checkId] });

  const addOne = useMutation({
    mutationFn: () =>
      addVerificationSample(checkId, { url: singleUrl.trim(), expected }),
    onSuccess: () => {
      setSingleUrl("");
      invalidate();
    },
    onError: (e: unknown) => {
      toast.error(
        e instanceof ApiError ? String(e.detail ?? e.message) : "Add failed",
      );
    },
  });

  const addBulk = useMutation({
    mutationFn: () =>
      bulkAddVerificationSamples(checkId, {
        expected,
        urls: bulkText.split(/\r?\n/).map((s) => s.trim()).filter(Boolean),
      }),
    onSuccess: (r) => {
      const summary = [
        r.created.length ? `${r.created.length} added` : null,
        r.skipped_duplicates.length
          ? `${r.skipped_duplicates.length} skipped`
          : null,
        r.rejected.length ? `${r.rejected.length} rejected` : null,
      ]
        .filter(Boolean)
        .join(", ");
      toast.success(summary || "No changes");
      setBulkText("");
      setBulkOpen(false);
      invalidate();
    },
  });

  return (
    <div className="space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-muted">
        {label} · {samples.length}
      </div>
      <div className="rounded-md border border-border overflow-hidden">
        {samples.length === 0 ? (
          <div className="p-3 text-xs text-muted">No samples yet.</div>
        ) : (
          samples.map((s) => (
            <SampleRow
              key={s.id}
              checkId={checkId}
              sample={s}
              outcome={outcomesBySample.get(s.id) ?? null}
            />
          ))
        )}
      </div>

      <div className="flex gap-2 items-center">
        <input
          className="input text-sm flex-1"
          placeholder="https://example.de"
          value={singleUrl}
          onChange={(e) => setSingleUrl(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && singleUrl.trim()) addOne.mutate();
          }}
        />
        <button
          className="btn-ghost text-sm"
          onClick={() => addOne.mutate()}
          disabled={!singleUrl.trim() || addOne.isPending}
        >
          <Plus className="h-4 w-4" /> Add
        </button>
        <button
          className="btn-ghost text-sm"
          onClick={() => setBulkOpen((v) => !v)}
        >
          Paste many
        </button>
      </div>

      {bulkOpen ? (
        <div className="space-y-2">
          <textarea
            className="input min-h-[8rem] font-mono text-xs"
            placeholder={"One URL per line.\nLines are normalized + deduped."}
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
          />
          <div className="flex justify-end">
            <button
              className="btn-primary text-sm"
              onClick={() => addBulk.mutate()}
              disabled={!bulkText.trim() || addBulk.isPending}
            >
              Add all
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SampleRow({
  checkId,
  sample,
  outcome,
}: {
  checkId: string;
  sample: VerificationSampleSummary;
  outcome: VerificationOutcome | null;
}) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["verification", checkId] });

  const del = useMutation({
    mutationFn: () => deleteVerificationSample(checkId, sample.id),
    onSuccess: invalidate,
  });
  const refresh = useMutation({
    mutationFn: () => refreshVerificationSample(checkId, sample.id),
    onSuccess: () => {
      toast.success("Cache cleared. Next verification will re-fetch.");
      invalidate();
    },
  });

  const cacheError =
    sample.cache_status === "fetch_error"
      ? sample.cache_unreachable_reason ?? "fetch_error"
      : null;

  return (
    <div className="px-3 py-2 border-b border-border last:border-b-0 flex items-start gap-2 text-sm">
      <VerdictIcon outcome={outcome} cacheError={cacheError} />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="truncate font-mono text-xs">
          <a
            href={sample.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-accent hover:underline"
          >
            {sample.normalized_url}
          </a>
        </div>
        {outcome ? <OutcomeDetail outcome={outcome} /> : null}
        {cacheError && !outcome ? (
          <div className="text-xs text-warn">
            fetch_error: <code>{cacheError}</code>
          </div>
        ) : null}
        {sample.note ? (
          <div className="text-xs text-muted">{sample.note}</div>
        ) : null}
      </div>
      <div className="flex items-center gap-1">
        <button
          className="btn-ghost text-xs"
          title="Refresh cache"
          onClick={() => refresh.mutate()}
          disabled={refresh.isPending}
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </button>
        <button
          className="btn-ghost text-xs text-danger"
          title="Delete sample"
          onClick={() => {
            if (window.confirm(`Delete ${sample.normalized_url}?`)) del.mutate();
          }}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function VerdictIcon({
  outcome,
  cacheError,
}: {
  outcome: VerificationOutcome | null;
  cacheError: string | null;
}) {
  if (!outcome) {
    if (cacheError) {
      return <AlertTriangle className="h-4 w-4 mt-0.5 text-warn" />;
    }
    return <div className="h-4 w-4 mt-0.5 rounded-full border border-border" />;
  }
  if (outcome.verdict === "pass") {
    return <CheckCircle2 className="h-4 w-4 mt-0.5 text-success" />;
  }
  if (outcome.verdict === "fail") {
    return <XCircle className="h-4 w-4 mt-0.5 text-danger" />;
  }
  return <AlertTriangle className="h-4 w-4 mt-0.5 text-warn" />;
}

function OutcomeDetail({ outcome }: { outcome: VerificationOutcome }) {
  if (outcome.verdict === "error") {
    return (
      <div className="text-xs text-warn">
        {outcome.unreachable_reason
          ? <>fetch_error: <code>{outcome.unreachable_reason}</code></>
          : outcome.harness_error
            ? <>harness_error: <code>{outcome.harness_error}</code></>
            : "error"}
      </div>
    );
  }
  const wordHits = outcome.word_hits;
  if (!wordHits || Object.keys(wordHits).length === 0) {
    if (outcome.matched_tags.length === 0) return null;
    return (
      <div className="text-xs text-muted">
        matched: {outcome.matched_tags.join(", ")}
      </div>
    );
  }
  return (
    <div className="flex flex-wrap gap-1 text-xs">
      {Object.entries(wordHits).map(([word, count]) => (
        <span
          key={word}
          className="rounded-md bg-subtle px-1.5 py-0.5 font-mono"
        >
          {word} ×{count}
        </span>
      ))}
    </div>
  );
}
